import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box, Button, Dialog, DialogContent, Alert
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import walletApi, { getPositionsByAssetClassV2 } from '../../api/wallet';
import { formatValue, getAssetInstrumentTypesByClass } from '../../utils/utils';
import OperationsForm from '../operations/OperationsForm';
import SortableTableHead from '../commons/SortableTableHead';
import useSorting from '../../utils/useSorting';
import { CHART_COLORS } from '../../constants/layout';

const PositionsByTypeTable = ({ currencyView }) => {
  const [positions, setPositions] = useState([]);
  const [totalPositions, setTotalPositions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allocationData, setAllocationData] = useState({});
  const [expandedRows, setExpandedRows] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);

  // Configuração das colunas para ordenação
  const columns = [
    { id: 'Asset.Class', label: 'Classe', sortable: true },
    { id: 'Percentage', label: '%Carteira', sortable: true },
    { id: 'TotalInvested', label: 'Total Investido', sortable: true },
    { id: 'CurrentValue', label: 'Valor Atual', sortable: true },
    { id: 'ProfitLoss', label: 'Lucro/Prejuízo', sortable: true },
    { id: 'ProfitLossPercentage', label: '%Lucro/Prejuízo', sortable: true },
    { id: 'details', label: 'Detalhes', sortable: false },
  ];

  // Transformadores de valor para ordenação
  const valueTransformers = {
    Percentage: (position) => parseFloat(position.percentage) || 0,
    TotalInvested: (position) => parseFloat(position.total_invested) || 0,
    CurrentValue: (position) => parseFloat(position.current_value) || 0,
    ProfitLoss: (position) => parseFloat(position.profit_loss) || 0,
    ProfitLossPercentage: (position) => parseFloat(position.profit_loss_percentage) || 0,
  };

  // Hook de ordenação
  const { orderBy, order, sortedData, handleRequestSort } = useSorting(positions, 'Asset.Class', 'asc', valueTransformers);

  const fetchAllocationData = async (currencyView) => {
    try {
      const response = await walletApi.get(`/Allocation/Suggestion/Distribution?CurrencyType=${currencyView}`);
      setAllocationData(response.data);
    } catch (err) {
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await getPositionsByAssetClassV2(currencyView);
      if (response.status === 204 || !response.data || !response.data.positions) {
        setPositions([]);
        setTotalPositions({});
        setError(null);
        return;
      }
      setPositions(Array.isArray(response.data.positions) ? response.data.positions : []);
      setTotalPositions(response.data.totals?.[0] || {});
      setError(null);
    } catch (err) {
      setError('Erro ao buscar posições.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPositions();
    fetchAllocationData(currencyView);
  }, [currencyView]);

  const toggleRowExpand = (type) => {
    setExpandedRows((prev) =>
      prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type]
    );
  };

  const calculateMargin = (dataset) => {
    const maxLabelLength = Math.max(...dataset.map((data) => data.name.length));
    return {
      left: maxLabelLength * 12,
      right: 10,
      top: 40,
      bottom: 30,
    };
  };

  const renderBarChart = () => {
    const dataset = sortedData.map((p) => ({
      name: p.asset_name,
      investido: parseFloat(p.total_invested || 0),
      atual: parseFloat(p.current_value || 0),
    }));

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Investimento vs Valor Atual ({currencyView})
        </Typography>
        <BarChart
          dataset={dataset}
          xAxis={[{ scaleType: 'band', dataKey: 'name' }]}
          series={[
            { dataKey: 'investido', label: 'Investido', color: CHART_COLORS[0] },
            { dataKey: 'atual', label: 'Atual', color: CHART_COLORS[1] },
          ]}
          height={350}
          margin={calculateMargin(dataset)}
          grid={{ horizontal: true }}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Só mostra o dashboard se houver posições */}
      {!loading && !error && positions.length > 0 && renderBarChart()}

      {loading ? (
        <Typography variant="h6">Carregando...</Typography>
      ) : error ? (
        <Typography variant="h6" color="error">{error}</Typography>
      ) : positions.length === 0 ? (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            Nenhuma posição encontrada. Cadastre uma nova operação para acompanhar seus investimentos.
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenDialog(true)}
            >
              Nova Operação
            </Button>
          </Box>
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
            <DialogContent>
              <OperationsForm onCreate={() => {
                fetchPositions();
                fetchAllocationData(currencyView);
                setOpenDialog(false);
              }} />
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table stickyHeader>
              <SortableTableHead
                columns={columns}
                orderBy={orderBy}
                order={order}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {sortedData.map((position, index) => {
                    const percentage = position.percentage || 'N/A';
                    const totalInvested = formatValue(position.total_invested, currencyView) || 'N/A';
                    const currentValue = formatValue(position.current_value, currencyView) || 'N/A';
                    const profitLoss = formatValue(position.profit_loss, currencyView) || 'N/A';
                    const profitLossPercentage = position.profit_loss_percentage || 'N/A';
                    const type = position.asset_class;

                    // Get raw numeric values for color logic
                    const rawProfitLoss = parseFloat(position.profit_loss) || 0;
                    const rawProfitLossPercentage = parseFloat(position.profit_loss_percentage) || 0;

                    return (
                      <React.Fragment key={index}>
                        <TableRow>
                          <TableCell>{type}</TableCell>
                          <TableCell>{percentage}%</TableCell>
                          <TableCell>{totalInvested}</TableCell>
                          <TableCell>{currentValue}</TableCell>
                          <TableCell sx={{ color: rawProfitLoss < 0 ? 'red' : 'green' }}>
                            {profitLoss}
                          </TableCell>
                          <TableCell sx={{ color: rawProfitLossPercentage < 0 ? 'red' : 'green' }}>
                            {profitLossPercentage}%
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => toggleRowExpand(type)}
                            >
                              {expandedRows.includes(type) ? 'Esconder' : 'Detalhes'}
                            </Button>
                          </TableCell>
                        </TableRow>

                        {expandedRows.includes(type) && (
                          <TableRow>
                            <TableCell colSpan={8}>
                              <Box
                                sx={{
                                  p: 2,
                                  bgcolor: (theme) => theme.palette.background.paper,
                                  borderRadius: 2,
                                  border: (theme) => `1px solid ${theme.palette.divider}`,
                                }}
                              >
                                <Typography variant="subtitle1" gutterBottom>
                                  Alocação Sugerida para {type}
                                </Typography>
                                <Table size="small" sx={{ mt: 1 }}>
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: (theme) => theme.palette.action.hover }}>
                                      <TableCell sx={{ fontWeight: 'bold' }}>Classe</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold' }}>Valor Sugerido</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold' }}>Valor de Ajuste</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold' }}>% Desejado</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    <TableRow>
                                      <TableCell>{type}</TableCell>
                                      <TableCell>{formatValue(allocationData?.SuggestedAllocation?.[type], currencyView)}</TableCell>
                                      <TableCell
                                        sx={{
                                          color: parseFloat(allocationData?.AdjustmentValue?.[type]) < 0 ? 'red' : 'green',
                                          fontWeight: 'bold',
                                        }}
                                      >
                                        {formatValue(allocationData?.AdjustmentValue?.[type], currencyView)}
                                      </TableCell>
                                      <TableCell>{allocationData?.DesiredPercentage?.[type]}%</TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>

                                {allocationData?.SuggestedAllocationByInstrument?.[type] &&
                                  Object.keys(allocationData.SuggestedAllocationByInstrument[type]).length > 0 && (
                                  <>
                                    <Typography variant="subtitle2" sx={{ mt: 3 }}>
                                      Detalhamento por tipo
                                    </Typography>
                                    <Table size="small" sx={{ mt: 1 }}>
                                      <TableHead>
                                        <TableRow sx={{ backgroundColor: (theme) => theme.palette.action.hover }}>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Valor Sugerido</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Valor de Ajuste</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>% Desejado</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {Object.keys(allocationData.SuggestedAllocationByInstrument[type]).map((instrumentType) => {
                                          const labels = getAssetInstrumentTypesByClass(type).reduce((acc, item) => {
                                            acc[item.value] = item.label;
                                            return acc;
                                          }, {});
                                          return (
                                            <TableRow key={instrumentType}>
                                              <TableCell>{labels[instrumentType] || instrumentType}</TableCell>
                                              <TableCell>
                                                {formatValue(
                                                  allocationData?.SuggestedAllocationByInstrument?.[type]?.[instrumentType],
                                                  currencyView
                                                )}
                                              </TableCell>
                                              <TableCell
                                                sx={{
                                                  color:
                                                    parseFloat(
                                                      allocationData?.AdjustmentValueByInstrument?.[type]?.[instrumentType]
                                                    ) < 0
                                                      ? 'red'
                                                      : 'green',
                                                  fontWeight: 'bold',
                                                }}
                                              >
                                                {formatValue(
                                                  allocationData?.AdjustmentValueByInstrument?.[type]?.[instrumentType],
                                                  currencyView
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                {allocationData?.DesiredPercentageByInstrument?.[type]?.[instrumentType]}%
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}

                {positions.length > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>100%</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{formatValue(totalPositions.total_invested, currencyView) || 'N/A'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{formatValue(totalPositions.current_value, currencyView) || 'N/A'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: (parseFloat(totalPositions.profit_loss) || 0) < 0 ? 'red' : 'green' }}>
                      {formatValue(totalPositions.profit_loss, currencyView) || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: (parseFloat(totalPositions.profit_loss_percentage) || 0) < 0 ? 'red' : 'green' }}>
                      {totalPositions.profit_loss_percentage || 'N/A'}%
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenDialog(true)}
            >
              Nova Operação
            </Button>
          </Box>
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
            <DialogContent>
              <OperationsForm onCreate={() => {
                fetchPositions();
                fetchAllocationData(currencyView);
                setOpenDialog(false);
              }} />
            </DialogContent>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default PositionsByTypeTable;